import React from "react";
import { CheckCircle2, Phone, Crown } from "lucide-react";
import { PHONES } from "../../content";

const PREMIUM_BG = "https://images.pexels.com/photos/32281630/pexels-photo-32281630.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";

export default function PremiumPlan({ t }) {
  return (
    <section id="premium" className="relative py-24 md:py-32 overflow-hidden" data-testid="premium-section">
      {/* Background image with overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <img src={PREMIUM_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-950/85 to-stone-950" />
        <div className="absolute top-1/2 right-0 w-[40rem] h-[40rem] rounded-full bg-amber-600/10 blur-3xl -translate-y-1/2" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-600 text-stone-950 text-xs uppercase tracking-[0.25em] font-medium mb-6">
              <Crown size={14} />
              {t.tag}
            </div>

            <div className="text-amber-500 text-xs uppercase tracking-[0.3em] mb-4">{t.eyebrow}</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-50 leading-tight">{t.title}</h2>
            <p className="mt-6 text-stone-300 text-base md:text-lg leading-relaxed max-w-2xl">{t.sub}</p>

            <div className="mt-10">
              <div className="text-amber-500 text-xs uppercase tracking-[0.3em] mb-5">{t.bullet_title}</div>
              <ul className="grid sm:grid-cols-2 gap-3">
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-200" data-testid={`premium-bullet-${i}`}>
                    <CheckCircle2 size={18} className="text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-base">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: stats + direct contact CTA */}
          <div className="lg:col-span-5 space-y-5">
            <div className="heritage-card relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-700" />
              <div className="p-8 md:p-10">
                <div className="grid grid-cols-2 gap-6">
                  {t.stats.map((s, i) => (
                    <div key={i} className="border-l border-amber-600/40 pl-4" data-testid={`premium-stat-${i}`}>
                      <div className="text-stone-400 text-[10px] uppercase tracking-[0.25em] mb-2">{s.label}</div>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <div className="font-serif text-2xl md:text-3xl text-stone-50 leading-none whitespace-nowrap">{s.value}</div>
                        {s.unit && <div className="text-stone-400 text-xs">{s.unit}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Direct call CTA panel — replaces the form button */}
            <div className="heritage-card p-7 md:p-8" data-testid="premium-contact-panel">
              <div className="flex items-center gap-2 text-amber-500 mb-3">
                <Crown size={16} />
                <span className="text-xs uppercase tracking-[0.25em]">{t.cta_title}</span>
              </div>
              <p className="text-stone-300 text-sm leading-relaxed mb-6">{t.cta_sub}</p>

              <div className="space-y-3">
                {PHONES.map((p) => (
                  <a
                    key={p}
                    href={`tel:${p}`}
                    data-testid={`premium-call-${p}`}
                    className="flex items-center gap-4 p-4 rounded-xl border border-amber-600/30 bg-amber-600/5 hover:bg-amber-600 hover:text-stone-950 transition-colors group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-amber-600 text-stone-950 flex items-center justify-center group-hover:bg-stone-950 group-hover:text-amber-500 transition-colors">
                      <Phone size={18} />
                    </div>
                    <div className="leading-tight">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-stone-400 group-hover:text-stone-900 transition-colors">{t.cta_label_call}</div>
                      <div className="font-serif text-xl text-stone-50 group-hover:text-stone-950 transition-colors">+91 {p}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
